import React, { useEffect, useRef, useState } from "react";
import {
  approvePendingIDCheck,
  getPendingIDCheckByID,
  verifyIdentity,
} from "../../utils/apis/func";
import { toast } from "react-toastify";
import { useAppDispatch } from "../../utils/redux/hooks";
import { addisLoading } from "../../utils/redux/mainSlice";
import { useNavigate, useParams } from "react-router";
import { Link } from "react-router-dom";
import NinReuseNotice from "../../components/NinReuseNotice";
import IDCheckAccount from "./IDCheckAccount";

function VerifyNIN() {
  const [nin, setNIN] = useState<string | null>(null);
  const [bvn, setBVN] = useState<string | null>(null);
  const [ninError, setNINError] = useState("");
  const [bvnError, setBVNError] = useState("");
  const [ninRes, setNinRes] = useState<any>();
  const [bvnRes, setBvnRes] = useState<any>();
  const [isLoading, setIsLoading] = useState(false);
  const [lookupError, setLookupError] = useState('');
  const lookupInFlight = useRef(false);
  const lookupGeneration = useRef(0);
  const query = useParams();
  const queryID = query.id || "";
  const navigate = useNavigate();

  const dispatch = useAppDispatch();

  const closeTab = () => {
    window.opener = null;
    window.open("", "_self");
    window.close();
  };

  const [idDetail, setIdDetails] = useState<any>();
  const [recordError, setRecordError] = useState('');
  const [recordLoading, setRecordLoading] = useState(true);
  // dispatch()
  useEffect(() => {
    dispatch(addisLoading(false));
  }, []);

  useEffect(() => {
    let active = true;
    lookupGeneration.current += 1;
    lookupInFlight.current = false;
    dispatch(addisLoading(false));
    setIsLoading(false);
    setLookupError('');
    setIdDetails(undefined); setNinRes(undefined); setBvnRes(undefined); setNIN(null); setBVN(null); setRecordError(''); setRecordLoading(true);
    getPendingIDCheckByID(queryID).then((res: any) => {
      if (!active) return;
      const record = res?.data?.data?.results?.[0];
      if (res?.status !== 200 || String(record?._id || record?.id) !== queryID) {setRecordError('This ID submission could not be loaded. Return to ID Check and refresh.'); return;}
      setIdDetails(record);
      if (record?.type?.toLowerCase() === 'nin' && /^\d{11}$/.test(record.number || '')) setNIN(record.number);
    }).catch(() => {if (active) setRecordError('Could not load this ID submission. Return to ID Check and refresh.');})
      .finally(() => {if (active) setRecordLoading(false);});
    return () => {active = false; lookupGeneration.current += 1;};
  }, [queryID]);
  const handleChangeNIN = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    if (/^\d*$/.test(value)) {
      setNinRes(undefined); setBvnRes(undefined); setLookupError('');
      setNIN(value || null);
      setNINError(
        value.length === 11 || value === ""
          ? ""
          : "NIN must be exactly 11 digits"
      );
    } else {
      setNINError("NIN must contain only digits");
    }
  };

  const handleChangeBVN = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    if (/^\d*$/.test(value)) {
      setNinRes(undefined); setBvnRes(undefined); setLookupError('');
      setBVN(value || null);
      setBVNError(
        value.length === 11 || value === ""
          ? ""
          : "BVN must be exactly 11 digits"
      );
    } else {
      setBVNError("BVN must contain only digits");
    }
  };

  const handleVerifyIdentity = async ({
    type,
    number,
  }: {
    type: string;
    number: string;
  }) => {
    if (lookupInFlight.current || isLoading || idDetail?.status !== 'pending' || !/^\d{11}$/.test(number)) return;
    lookupInFlight.current = true;
    const generation = lookupGeneration.current;
    setLookupError('');
    setNinRes(undefined);
    setBvnRes(undefined);
    try {
      dispatch(addisLoading(true));
      setIsLoading(true);
      const res: any = await verifyIdentity({ type, number });
      if (generation !== lookupGeneration.current) return;
      if (res?.status === 200 || res?.status === 201) {
        const details = type === 'nin' ? res?.data?.data?.nin_data : res?.data?.data?.data;
        if (!details || typeof details !== 'object' || Array.isArray(details) || Object.keys(details).length === 0) {
          setLookupError('The identity partner returned no identity details. The check is not complete. Contact support before retrying.');
          return;
        }
        toast("Details fetched successfully", {
          position: "top-right",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: "light",
        });
        setNinRes(null);
        setBvnRes(null);
        if (type === "nin") {
          setNinRes(res?.data?.data?.nin_data);
        } else if (type === "bvn") {
          setBvnRes(res?.data?.data?.data);
        }
      } else {
        const message = typeof res?.error?.message === 'string' ? res.error.message
          : res?.status === 401 ? 'Your admin session has expired. Log in again before checking this ID.'
          : res?.status === 403 ? 'Your admin account does not have permission to check this ID.'
          : 'Could not reach the ID-check service. Check your connection and try again. The ID has not been marked invalid.';
        setLookupError(message);
        toast.error(message);
      }
    } catch {
      if (generation !== lookupGeneration.current) return;
      setLookupError('Could not complete the ID lookup. Check your connection and try again. The ID has not been marked invalid.');
    } finally {
      if (generation === lookupGeneration.current) {
        lookupInFlight.current = false;
        setIsLoading(false);
        dispatch(addisLoading(false));
      }
    }
  };


  const handleVerifyIdentityDetails = async () => {
    if (isLoading || !idDetail || idDetail.status !== "pending" || String(idDetail._id || idDetail.id) !== queryID) return;
    try {
      dispatch(addisLoading(true));
      setIsLoading(true);
      const res: any = await approvePendingIDCheck(query?.id);
      if (res?.status === 200 || res?.status === 201) {
        toast("Verified Successfully", {
          position: "top-right",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: "light",
        });
        navigate("/id-check");
        setNIN("");
        setBVN("");
      } else {toast.error(res?.error?.message || 'Could not approve this ID check. Refresh and try again.');}

    } catch (error) {
      console.error("Error occurred during API call:", error);

      toast("An error occurred. Please try again", {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "dark",
      });
    } finally {
      setIsLoading(false);
      dispatch(addisLoading(false));
      setNIN("");
      setBVN("");
    }
  };


  const isNINValid = nin?.length === 11 && /^\d+$/.test(nin || "");
  const isBVNValid = bvn?.length === 11 && /^\d+$/.test(bvn || "");
  if (recordLoading || (idDetail && String(idDetail._id || idDetail.id) !== queryID)) return <p className="p-6">Loading ID submission...</p>;
  if (recordError) return <p role="alert" className="p-6">{recordError}</p>;
  return (
    <div className="w-full h-full ">
      <div className="p-4">
        <Link to="/id-check" className="text-purple-800 underline">Back to ID Check</Link>
        <h1 className="mt-3 text-xl font-semibold">ID check details</h1>
      </div>
      <IDCheckAccount key={queryID} user={idDetail?.user} />
      <NinReuseNotice evidence={idDetail?.ninReuse} />
      {lookupError && <p role="alert" className="m-4 rounded border border-red-300 bg-red-50 p-4 text-red-800">{lookupError}</p>}
      {idDetail && <p className="p-4">Submitted ID: {idDetail.type?.toUpperCase()} {idDetail.number} · {idDetail.status}</p>}
      <div className="flex justify-center items-center w-full my-8 px-4">
        <div className="space-y-6">
          <div className="max-w-lg flex items-center gap-8">
            <div className="relative w-full">
              <div className="absolute top-0 left-0 pt-2 flex items-center pl-3">
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 20 20"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M16.6668 17.5V15.8333C16.6668 14.9493 16.3156 14.1014 15.6905 13.4763C15.0654 12.8512 14.2176 12.5 13.3335 12.5H6.66683C5.78277 12.5 4.93493 12.8512 4.30981 13.4763C3.68469 14.1014 3.3335 14.9493 3.3335 15.8333V17.5"
                    stroke="black"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M9.99984 9.16667C11.8408 9.16667 13.3332 7.67428 13.3332 5.83333C13.3332 3.99238 11.8408 2.5 9.99984 2.5C8.15889 2.5 6.6665 3.99238 6.6665 5.83333C6.6665 7.67428 8.15889 9.16667 9.99984 9.16667Z"
                    stroke="black"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
              <input
                type="text"
                id="nin"
                name="nin"
                value={nin || ""}
                disabled={isLoading}
                onChange={handleChangeNIN}
                className="focus:outline-none px-14 w-full bg-[#fff] border border-[#000] rounded-md py-2 text-[#000] font-montserrat text-xs font-light leading-5 tracking-wide uppercase"
                placeholder="Enter NIN"
              />
              {ninError && (
                <p className="text-red-500 text-xs mt-1">{ninError}</p>
              )}
            </div>
            <button
              onClick={() => {
                handleVerifyIdentity({ number: nin || "", type: "nin" });
              }}
              className={`w-max whitespace-nowrap bg-black text-white rounded-md px-6 py-2 ${
                !isNINValid || isLoading ? "opacity-50 cursor-not-allowed" : ""
              }`}
              disabled={!isNINValid || isLoading || idDetail?.status !== "pending"}
            >
              {isLoading ? "Fetching Data" : "Go"}
            </button>
          </div>
          <div className="max-w-lg flex items-center gap-8">
            <div className="relative w-full">
              <div className="absolute top-0 left-0 pt-2 flex items-center  pl-3">
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 20 20"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M16.6668 17.5V15.8333C16.6668 14.9493 16.3156 14.1014 15.6905 13.4763C15.0654 12.8512 14.2176 12.5 13.3335 12.5H6.66683C5.78277 12.5 4.93493 12.8512 4.30981 13.4763C3.68469 14.1014 3.3335 14.9493 3.3335 15.8333V17.5"
                    stroke="black"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M9.99984 9.16667C11.8408 9.16667 13.3332 7.67428 13.3332 5.83333C13.3332 3.99238 11.8408 2.5 9.99984 2.5C8.15889 2.5 6.6665 3.99238 6.6665 5.83333C6.6665 7.67428 8.15889 9.16667 9.99984 9.16667Z"
                    stroke="black"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
              <input
                type="text"
                id="bvn"
                name="bvn"
                value={bvn || ""}
                disabled={isLoading}
                onChange={handleChangeBVN}
                className="focus:outline-none px-14 w-full bg-[#fff] border border-[#000] rounded-md py-2 text-[#000] font-montserrat text-xs font-light leading-5 tracking-wide uppercase"
                placeholder="Enter BVN"
              />
              {bvnError && (
                <p className="text-red-500 text-xs mt-1">{bvnError}</p>
              )}
            </div>
            <button
              onClick={() => {
                handleVerifyIdentity({ number: bvn || "", type: "bvn" });
              }}
              className={`w-max whitespace-nowrap bg-black text-white rounded-md px-6 py-2 ${
                !isBVNValid || isLoading ? "opacity-50 cursor-not-allowed" : ""
              }`}
              disabled={!isBVNValid || isLoading || idDetail?.status !== "pending"}
            >
              {isLoading ? "Fetching Data" : "Go"}
            </button>
          </div>
        </div>
      </div>

      <section aria-labelledby="identity-result-heading" className="w-full my-8 p-4 sm:p-8 border-t">
        <h2 id="identity-result-heading" className="text-lg font-semibold mb-4">Identity lookup result</h2>
        {!ninRes && !bvnRes ? (
          <p className="text-[#606060] text-center font-roboto text-[16px] font-medium leading-normal mt-4">
            {isLoading ? "Fetching Data" : "No Data Available"}
          </p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-8 max-w-7xl mx-auto">
            <div className="mb-6">
              <p className="text-[#606060] font-roboto text-[16px] font-medium leading-normal mt-4 border-[#000] px-4">
                First Name
              </p>
              <p className="text-[#000] font-roboto text-[16px] font-medium leading-normal mt-2 border-[#000] px-4">
                {ninRes?.firstname || bvnRes?.firstName || "nil"}
              </p>
            </div>
            <div className="mb-6">
              <p className="text-[#606060] font-roboto text-[16px] font-medium leading-normal mt-4 border-[#000] px-4">
                Middle Name
              </p>
              <p className="text-[#000] font-roboto text-[16px] font-medium leading-normal mt-2 border-[#000] px-4">
                {ninRes?.middlename || bvnRes?.middleName || "nil"}
              </p>
            </div>
            <div className="mb-6">
              <p className="text-[#606060] font-roboto text-[16px] font-medium leading-normal mt-4 border-[#000] px-4">
                Surname
              </p>
              <p className="text-[#000] font-roboto text-[16px] font-medium leading-normal mt-2 border-[#000] px-4">
                {ninRes?.surname || bvnRes?.lastName || "nil"}
              </p>
            </div>
            <div className="mb-6">
              <p className="text-[#606060] font-roboto text-[16px] font-medium leading-normal mt-4 border-[#000] px-4">
                Date of Birth
              </p>
              <p className="text-[#000] font-roboto text-[16px] font-medium leading-normal mt-2 border-[#000] px-4">
                {ninRes?.birthdate || bvnRes?.dateOfBirth || "nil"}
              </p>
            </div>
            <div className="mb-6">
              <p className="text-[#606060] font-roboto text-[16px] font-medium leading-normal mt-4 border-[#000] px-4">
                Phone Number
              </p>
              <p className="text-[#000] font-roboto text-[16px] font-medium leading-normal mt-2 border-[#000] px-4">
                {ninRes?.telephoneno || bvnRes?.phoneNumber1 || "nil"}
              </p>
            </div>
            <div className="mb-6">
              <p className="text-[#606060] font-roboto text-[16px] font-medium leading-normal mt-4 border-[#000] px-4">
                Email
              </p>
              <p className="text-[#000] font-roboto text-[16px] font-medium leading-normal mt-2 border-[#000] px-4">
                {ninRes?.email || bvnRes?.email || "nil"}
              </p>
            </div>
            <div className="col-span-full sm:col-span-2 md:col-span-4 text-center mb-6">
              <button
                onClick={handleVerifyIdentityDetails}
                disabled={isLoading || idDetail?.status !== "pending"}
                className="w-max whitespace-nowrap bg-purple-700 hover:bg-purple-500 text-white rounded-md px-6 py-2"
              >
                Verify
              </button>
            </div>
          </div>
        )}
      </section>
    </div>
  );
}

export default VerifyNIN;
